/**
 * Select mode chrome for the collection grid.
 *
 * A thin strip at the top ("3 selected", Select all, Done) and an action bar
 * that slides up over the bottom nav, because the nav is dead weight while
 * you are choosing cards. One tap on an action applies it to the selection.
 */

import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Handshake, Shield, Eye, EyeOff, Trash2, FolderInput } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

export function SelectionTopStrip({ open, count, total, onSelectAll, onDone }) {
  const { theme } = useTheme();
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
          style={{ ...styles.strip, backgroundColor: theme.bgCard, borderBottom: `1px solid ${theme.border}` }}
        >
          <span style={{ ...styles.count, color: theme.text }}>
            {count} selected
          </span>
          <div style={styles.stripButtons}>
            <button
              style={{ ...styles.stripBtn, color: theme.accent }}
              onClick={count === total ? undefined : onSelectAll}
              disabled={count === total}
            >
              {count === total ? 'All selected' : 'Select all'}
            </button>
            <button style={{ ...styles.stripBtn, ...styles.doneBtn, backgroundColor: theme.accent }} onClick={onDone}>
              Done
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const ACTIONS = [
  { id: 'tradeable', label: 'Tradable', Icon: Handshake },
  { id: 'pc', label: 'PC', Icon: Shield },
  { id: 'public', label: 'Public', Icon: Eye },
  { id: 'private', label: 'Private', Icon: EyeOff },
  { id: 'binder', label: 'Binder', Icon: FolderInput },
  { id: 'delete', label: 'Delete', Icon: Trash2 },
];

export function SelectionActionBar({ open, count, total, busy, onAction, onSelectAll, onDone }) {
  const { theme } = useTheme();
  const disabled = count === 0 || busy;
  // Rendered on document.body. Inside the page scroller iOS WebKit treats a
  // fixed element like an absolute one and the bar never clears the nav.
  if (typeof document === 'undefined') return null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: 'spring', damping: 26, stiffness: 320 }}
          style={{ ...styles.bar, backgroundColor: theme.bgSheet, borderTop: `1px solid ${theme.border}` }}
        >
          <div style={styles.barHead}>
            <span style={{ ...styles.count, color: theme.text }}>{count} selected</span>
            <div style={styles.stripButtons}>
              <button
                style={{ ...styles.stripBtn, color: theme.accent, opacity: count === total ? 0.5 : 1 }}
                onClick={count === total ? undefined : onSelectAll}
                disabled={count === total}
              >
                {count === total ? 'All selected' : 'Select all'}
              </button>
              <button style={{ ...styles.stripBtn, ...styles.doneBtn, backgroundColor: theme.accent }} onClick={onDone}>
                Done
              </button>
            </div>
          </div>
          <div style={styles.actions}>
          {ACTIONS.map(({ id, label, Icon }) => {
            const danger = id === 'delete';
            return (
              <button
                key={id}
                style={{
                  ...styles.action,
                  color: danger ? theme.error : theme.text,
                  opacity: disabled ? 0.4 : 1,
                }}
                onClick={() => !disabled && onAction(id)}
                disabled={disabled}
                aria-label={label}
              >
                <Icon size={20} strokeWidth={1.9} />
                <span style={styles.actionLabel}>{label}</span>
              </button>
            );
          })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

const styles = {
  strip: {
    position: 'sticky',
    top: 0,
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '8px 14px',
  },
  count: {
    fontSize: '14px',
    fontWeight: '600',
    fontVariantNumeric: 'tabular-nums',
  },
  stripButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  stripBtn: {
    background: 'none',
    border: 'none',
    padding: '7px 10px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  doneBtn: {
    color: '#fff',
  },
  bar: {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10001,
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 6px calc(10px + var(--safe-area-bottom, 0px))',
    boxShadow: '0 -6px 24px rgba(0, 0, 0, 0.35)',
  },
  barHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 10px 10px',
  },
  actions: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-around',
  },
  action: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 2px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  actionLabel: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.2px',
  },
};
